/**
 * Admin konzole — rt-* sidebar variant.
 * Zůstává separate od AppShell, protože má jiné nav items a zvýrazněný
 * "režim administrace" banner. Používá rt-* tokeny a utility.
 */

import React, { Fragment, useState } from 'react';
import {
  ArrowLeft,
  BookOpen,
  Cpu as DevicesIcon,
  FileText as AuditIcon,
  LayoutDashboard,
  LogOut as Logout,
  Menu as MenuIcon,
  Monitor as HardwareIcon,
  Moon,
  Network,
  ShieldAlert,
  Sun,
  Upload as FirmwareIcon,
  Users as UsersIcon,
  X as CloseIcon,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { clsx } from 'clsx';

import { useAuth } from '../../hooks/auth';
import { useThemeContext } from '../../hooks/theme';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const adminMenuItems = [
  { text: 'Přehled administrace', icon: LayoutDashboard, path: '/admin' },
  { text: 'Správa uživatelů', icon: UsersIcon, path: '/admin/users' },
  { text: 'Správa zařízení', icon: DevicesIcon, path: '/admin/devices' },
  { text: 'HW Metriky', icon: HardwareIcon, path: '/admin/hardware-metrics' },
  { text: 'Auditní logy', icon: AuditIcon, path: '/admin/audit-logs' },
  { text: 'Firmware (OTA)', icon: FirmwareIcon, path: '/admin/firmware' },
  { text: 'API přehled', icon: Network, path: '/admin/api-dashboard' },
  { text: 'Znalostní zdroje', icon: BookOpen, path: '/admin/knowledge-sources' },
];

const RtLogo: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <div
    className="relative flex items-center justify-center shrink-0"
    style={{ width: size, height: size }}
  >
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      className="text-[var(--rt-brand-500)]"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="30" height="30" rx="8" fill="currentColor" opacity="0.12" />
      <path d="M16 5 C10 12, 10 20, 16 27 C22 20, 22 12, 16 5 Z" fill="currentColor" />
      <path
        d="M16 27 L16 14"
        stroke="#fff"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.9"
      />
      <path
        d="M12 17 L16 20 M20 17 L16 20"
        stroke="#fff"
        strokeWidth="1.25"
        strokeLinecap="round"
        opacity="0.9"
      />
    </svg>
  </div>
);

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleColorMode, mode } = useThemeContext();
  const { user, logout } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const SidebarContent: React.FC = () => (
    <div className="relative flex flex-col h-full overflow-hidden border-r rt-divider bg-white/60 dark:bg-[#0f1412]/50 backdrop-blur">
      <div className="h-[var(--rt-topbar-h)] flex items-center gap-2.5 px-4 border-b rt-divider">
        <RtLogo size={30} />
        <div className="leading-none">
          <div className="font-semibold text-[15px] rt-display tracking-tight">Rootify</div>
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[var(--rt-brand-600)] dark:text-[var(--rt-brand-300)] mt-1">
            Administrace
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5" aria-label="Administrace">
        <div className="px-3 mt-1 mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--rt-ink-500)] dark:text-zinc-500">
          Správa systému
        </div>
        {adminMenuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              type="button"
              onClick={() => handleNavigation(item.path)}
              className={clsx(
                'w-full flex items-center gap-3 rt-h-row px-3 rounded-[10px] text-[13.5px] text-left transition-colors rt-focus-visible',
                isActive
                  ? 'bg-[var(--rt-brand-50)] text-[var(--rt-brand-700)] font-medium dark:bg-[var(--rt-brand-500)]/15 dark:text-[var(--rt-brand-200)]'
                  : 'text-[var(--rt-ink-600)] dark:text-zinc-400 hover:bg-black/[.04] dark:hover:bg-white/5',
              )}
            >
              <Icon size={18} />
              <span>{item.text}</span>
            </button>
          );
        })}
      </nav>

      <div className="border-t rt-divider p-3">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="w-full flex items-center gap-2 h-10 px-3 rounded-[10px] bg-[var(--rt-brand-600)] hover:bg-[var(--rt-brand-700)] text-white text-[13px] font-medium transition-colors mb-3"
        >
          <ArrowLeft size={16} />
          <span>Zpět do aplikace</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--rt-brand-400)] to-[var(--rt-brand-700)] text-white flex items-center justify-center text-[11px] font-semibold">
            {user?.username?.charAt(0) || 'A'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-medium truncate">{user?.username}</div>
            <div className="text-[10.5px] text-[var(--rt-ink-500)] dark:text-zinc-500 truncate">
              {user?.email}
            </div>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            aria-label="Odhlásit se"
            className="p-1.5 rounded-md text-[var(--rt-ink-500)] hover:text-red-500 dark:text-zinc-400 dark:hover:text-red-300"
          >
            <Logout size={15} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-[var(--rt-bg)] text-[var(--rt-ink-900)] dark:text-zinc-100">
      {/* Mobile Drawer */}
      <Transition.Root show={mobileOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <div className="absolute top-0 right-0 -mr-12 pt-4">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setMobileOpen(false)}
                  >
                    <span className="sr-only">Zavřít boční panel</span>
                    <CloseIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <SidebarContent />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-[260px] lg:flex-col">
        <SidebarContent />
      </div>

      {/* Main Content */}
      <div className="relative z-10 lg:pl-[260px] flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 h-[var(--rt-topbar-h)] flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b rt-divider bg-white/70 dark:bg-[#0f1412]/70 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <button
              type="button"
              className="p-2 rounded-lg text-[var(--rt-ink-500)] dark:text-zinc-400 hover:bg-black/[.04] dark:hover:bg-white/5 lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Otevřít boční panel"
            >
              <MenuIcon className="h-5 w-5" />
            </button>
            <div className="lg:hidden flex items-center gap-2">
              <RtLogo size={26} />
              <span className="font-semibold rt-display text-[15px]">Administrace</span>
            </div>
            <div className="hidden sm:inline-flex items-center gap-2 h-7 px-3 rounded-full bg-[var(--rt-brand-50)] text-[var(--rt-brand-700)] dark:bg-[var(--rt-brand-500)]/15 dark:text-[var(--rt-brand-200)] text-[11px] font-bold uppercase tracking-[0.14em]">
              <ShieldAlert size={14} />
              Režim administrace
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleColorMode}
              aria-label={mode === 'dark' ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
              className="p-2 rounded-lg hover:bg-black/[.04] dark:hover:bg-white/5 text-[var(--rt-ink-500)] dark:text-zinc-400"
            >
              {mode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</main>
      </div>
    </div>
  );
};
